import React, { CSSProperties } from "react";
import { useDndContext } from "@dnd-kit/core";
import { useSortable } from "@dnd-kit/sortable";
import { Button, Space, theme } from "antd";
import Tooltip from "../../../../Common/Tooltip";
import {
  ArrowDownOutlined,
  SendOutlined,
  FacebookOutlined,
  InstagramOutlined,
  LinkedinOutlined,
  TikTokOutlined,
  WhatsAppOutlined,
  XOutlined,
  YoutubeOutlined,
  ArrowUpOutlined,
  CopyOutlined,
  DeleteOutlined,
  HolderOutlined,
  PictureOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import {
  Block,
  EmailStyles,
  SocialNetwork,
  BUTTON_RADIUS,
  buttonSize,
  INK_ON,
  groundOf,
  Ground,
  GROUND_LINE,
  GROUND_INK,
  DEFAULT_SOCIAL_SIZE,
  socialGap,
  BlockVariant,
  DEFAULT_VARIANT,
  ALIGN_JUSTIFY,
  BlockAlign,
  cornersCss,
  ratioValue,
  evenCorners,
  edgesCss,
  DEFAULT_IMAGETEXT_RATIO,
  SPACER_DEFAULT,
} from "../../blocks";

import styles from "./index.module.scss";

/**
 * Seconds before a tip appears. antd's own default is 0.1, which fires while
 * the pointer is only crossing the panel on its way somewhere else; half a
 * second waits for the pointer to actually stop on a button.
 *
 * Set here rather than globally because ConfigProvider's tooltip config does
 * not carry delays — only class, style, arrow and trigger.
 */
/**
 * What an Image block with no picture yet stands in at: a wordmark's 168 × 56
 * at its original size, and a 3:2 band otherwise.
 */
const PLACEHOLDER_ORIGINAL = { width: 168, height: 56 };
const PLACEHOLDER_RATIO = 3 / 2;

/** How wide the picture sits in an Image block, given its fit. */
function imageWidth(block: Block) {
  switch (block.fit ?? "fill") {
    case "original":
      // Its own size: the file decides, and a block without one stands in at
      // the wordmark width.
      return block.image ? "auto" : PLACEHOLDER_ORIGINAL.width;
    case "scale":
      return `${block.scale ?? 50}%`;
    default:
      // Fill: the whole width the block's padding leaves.
      return "100%";
  }
}

/** Where a picture narrower than its block sits, as margins. */
const IMAGE_MARGIN: Record<BlockAlign, string> = {
  start: "0 auto 0 0",
  center: "0 auto",
  end: "0 0 0 auto",
};

/**
 * The thinnest a spacer goes. Below this it stops being something a pointer can
 * find: the section has no content of its own, so its height is the whole of
 * what there is to click on or take hold of.
 */
export const SPACER_MIN = 12;

/**
 * The mark for each network, from antd's own brand set.
 *
 * antd's "Outlined" set throughout, and not by taste. Half its "Filled" social
 * glyphs are not marks at all but badges — FacebookFilled, LinkedinFilled and
 * XFilled are a full-bleed rounded square with the letter cut out of it, using
 * `fill-rule: evenodd`. Colour paints the square, so setting a section's icon
 * colour to white gave a solid white block with the ground showing through the
 * "f", standing beside an Instagram camera drawn as line art. One row, two
 * different things.
 *
 * The outlined variants are line art all the way across, and the shape behind a
 * glyph then belongs to Icon style, which is what that setting is for.
 *
 * TODO(design): Facebook and LinkedIn are still square-shaped even outlined —
 * antd ships no bare "f" or "in". See DESIGNER_NOTES.
 */
const SOCIAL_ICONS: Record<SocialNetwork, React.ReactNode> = {
  facebook: <FacebookOutlined />,
  instagram: <InstagramOutlined />,
  x: <XOutlined />,
  linkedin: <LinkedinOutlined />,
  youtube: <YoutubeOutlined />,
  tiktok: <TikTokOutlined />,
  whatsapp: <WhatsAppOutlined />,
  // antd ships no Telegram glyph; the paper plane is what it offers for a
  // message sent, and is the closest thing in the set.
  telegram: <SendOutlined />,
};

/**
 * How a social icon is drawn: the glyph on its own, a filled button behind it,
 * or an outlined one.
 */
/**
 * How a button or a social icon is drawn on a given ground.
 *
 * The glyph is the ground's text colour — an icon is a word in a section, and
 * Icon color offers exactly what Text offers. It used to default to the brand,
 * which disagreed with the panel: the panel marked the text colour as chosen
 * and the letter drew the brand.
 *
 * The outline is the ground's rule colour, the one a divider draws across the
 * letter — except on the brand, which has no rule colour of its own and takes
 * the same colour as its words.
 *
 * Fill makes its own ground and reads neither: the glyph on it pairs with the
 * fill, which is settled where the fill is.
 */
/** What a block is drawn as, before it has been told. */
const variantOf = (block: Block): BlockVariant =>
  block.variant ?? DEFAULT_VARIANT[block.type] ?? "fill";

const VARIANT_STYLE: Record<
  BlockVariant,
  (styles: EmailStyles, ground: Ground) => CSSProperties
> = {
  plain: (styles, ground) => ({ color: styles[GROUND_INK[ground]] }),
  // The brand, which is what the panel shows as chosen when a block has picked
  // no fill of its own. A section that names one overrides this below.
  fill: (styles) => ({ background: styles.brand, color: styles.textOnBrand }),
  outline: (styles, ground) => ({
    color: styles[GROUND_INK[ground]],
    border: `1px solid ${styles[GROUND_LINE[ground]]}`,
  }),
};

/** The three grounds a section can sit on, each read from the letter. */
const BACKGROUNDS: Record<
  NonNullable<Block["bg"]>,
  (styles: EmailStyles) => string
> = {
  Default: (styles) => styles.background,
  Soft: (styles) => styles.subtleBackground,
  Email: (styles) => styles.emailBackground,
  Accent: (styles) => styles.accent,
  Brand: (styles) => styles.brand,
};

interface LetterBlockProps {
  block: Block;
  emailStyles: EmailStyles;
  selected: boolean;
  preview: boolean;
  onSelect: () => void;
  onMove: (direction: -1 | 1) => void;
  onDuplicate: () => void;
  onRemove: () => void;
  /** False on the last section: a letter always keeps one. */
  canRemove: boolean;
  /** False at the ends of the letter, where there is nowhere to move. */
  canMoveUp: boolean;
  canMoveDown: boolean;
}

/**
 * A picture that leads somewhere. Without a link it adds nothing to the markup,
 * so a section that has none renders exactly as it did.
 */
function Linked({
  link,
  children,
}: {
  link?: string;
  children: React.ReactNode;
}) {
  // "https://" is what a new section starts with — a field waiting to be filled
  // rather than an address, and not something to wrap a picture in.
  if (!link || link === "https://") return <>{children}</>;

  return (
    <a href={link} className={styles.imageLink}>
      {children}
    </a>
  );
}

/**
 * The text as it was written. A blank line starts a new paragraph, and a single
 * newline is a break inside one — which is what `pre-line` on `.text` keeps.
 * Whitespace-only paragraphs are dropped, so trailing returns at the end of the
 * field do not leave gaps in the letter.
 */
function Paragraphs({ text = "" }: { text?: string }) {
  const paragraphs = text.split(/\n{2,}/).filter((part) => part.trim() !== "");

  return (
    <>
      {(paragraphs.length ? paragraphs : [""]).map((paragraph, index) => (
        // Index as key: paragraphs have no identity of their own, and the whole
        // text is re-read on every change anyway.
        <p key={index} className={styles.text}>
          {paragraph}
        </p>
      ))}
    </>
  );
}

/**
 * The block's own content. Never interactive — a click anywhere selects it.
 *
 * Exported so a thumbnail can draw a letter without any of the builder around
 * it: LetterBlock registers with dnd-kit, and a preview inside the Blocks panel
 * would land in the builder's own drag context.
 */
export function BlockContent({
  block,
  emailStyles,
  ink,
}: {
  block: Block;
  emailStyles: EmailStyles;
  /** The colour the block's words are read in, already resolved. */
  ink: string;
}) {
  switch (block.type) {
    case "logo":
      return <div className={styles.logo}>{block.text}</div>;
    case "header":
      return <h2 className={styles.header}>{block.text}</h2>;
    case "title":
      return <h3 className={styles.subheader}>{block.text}</h3>;
    case "button":
      return (
        <div
          className={styles.buttonRow}
          style={{ justifyContent: ALIGN_JUSTIFY[block.align ?? "center"] }}
        >
          <span
            className={styles.button}
            style={{
              // Medium unless the section says otherwise. Width and type size
              // come with the height, so a larger button is larger throughout
              // rather than a taller box around the same words.
              minHeight: buttonSize(block.size).height,
              minWidth: buttonSize(block.size).width,
              fontSize: buttonSize(block.size).fontSize,
              borderRadius: cornersCss(
                block.itemRadius ?? evenCorners(BUTTON_RADIUS),
              ),
              // Filled, outlined or neither — the same three shapes a social
              // icon comes in, from the same table.
              ...VARIANT_STYLE[variantOf(block)](emailStyles, groundOf(block)),
              // A fill chosen for the section wins over the shape's own, and so
              // does a colour chosen for its words.
              ...(variantOf(block) === "fill"
                ? {
                    background:
                      block.fillCustom ??
                      (block.fill
                        ? emailStyles[block.fill]
                        : emailStyles.brand),
                  }
                : {}),
              color: ink,
            }}
          >
            {block.text}
          </span>
        </div>
      );
    case "divider":
      return (
        <hr
          className={styles.divider}
          style={{
            // The thickness the section is set to. It lives here rather than in
            // the stylesheet because it is the section's own — a rule written
            // there could only ever be one width, which is what the Thickness
            // field had been quietly writing into for some time.
            borderTopWidth: block.size ?? 1,
            // The letter's own rule colour unless the section names another of
            // its colours, or carries one of its own — and which of the two
            // rules that is depends on the ground, the same way every other
            // colour in a block does. The pale one is invisible on the accent.
            // A ground picked by hand has no pairing, so it keeps the sheet's.
            borderTopColor:
              block.lineCustom ??
              emailStyles[block.line ?? GROUND_LINE[groundOf(block)]],
          }}
        />
      );
    case "spacer":
      return (
        <div
          style={{ height: Math.max(block.size ?? SPACER_DEFAULT, SPACER_MIN) }}
        />
      );
    case "image": {
      const fit = block.fit ?? "fill";
      const corners = cornersCss(block.itemRadius ?? emailStyles.imageRadius);
      const margin =
        fit === "fill" ? undefined : IMAGE_MARGIN[block.imageAlign ?? "center"];

      // A file keeps the shape it was cropped to, so it is an <img> and its own
      // proportion decides the height; a block without one has a placeholder to
      // draw, which needs a shape given to it.
      return (
        <Linked link={block.link}>
          {block.image ? (
            <img
              src={block.image}
              alt={block.alt ?? ""}
              className={styles.image}
              style={{
                width: imageWidth(block),
                height: "auto",
                margin,
                borderRadius: corners,
              }}
            />
          ) : (
            <div
              className={styles.image}
              style={{
                width: imageWidth(block),
                ...(fit === "original"
                  ? { height: PLACEHOLDER_ORIGINAL.height }
                  : { aspectRatio: PLACEHOLDER_RATIO }),
                margin,
                borderRadius: corners,
              }}
              role="img"
              aria-label={block.alt}
            >
              {/* A glyph rather than a bare grey band: the block reads as a
                  picture waiting for one. */}
              <PictureOutlined className={styles.placeholderIcon} />
            </div>
          )}
        </Linked>
      );
    }
    case "imagetext":
      return (
        <div
          className={styles.imagetext}
          // The picture leads by default; Right swaps the two columns without
          // moving either in the markup, so what a reader hears keeps its order.
          style={block.imageSide === "right" ? { direction: "rtl" } : undefined}
        >
          <Linked link={block.link}>
            {/* The picture takes its share of the block, in the block's own
                ratio; a file is cropped to that same shape. */}
            {block.image ? (
              <img
                src={block.image}
                alt={block.alt ?? ""}
                className={styles.image}
                style={{
                  width: "100%",
                  aspectRatio: ratioValue(
                    block.ratio ?? DEFAULT_IMAGETEXT_RATIO,
                  ),
                  objectFit: "cover",
                  borderRadius: cornersCss(
                    block.itemRadius ?? emailStyles.imageRadius,
                  ),
                }}
              />
            ) : (
              <div
                className={styles.image}
                style={{
                  width: "100%",
                  aspectRatio: ratioValue(
                    block.ratio ?? DEFAULT_IMAGETEXT_RATIO,
                  ),
                  borderRadius: cornersCss(
                    block.itemRadius ?? emailStyles.imageRadius,
                  ),
                }}
                role="img"
                aria-label={block.alt}
              >
                <PictureOutlined className={styles.placeholderIcon} />
              </div>
            )}
          </Linked>
          <div style={{ direction: "ltr" }}>
            <Paragraphs text={block.text} />
          </div>
        </div>
      );
    case "social": {
      // Square buttons, at whichever of the three sides the section is set to.
      const side = block.size ?? DEFAULT_SOCIAL_SIZE;

      return (
        <p className={styles.centered}>
          {(block.socials ?? []).map((social) => (
            // A link, not a button: in an email this is an anchor, and the alt
            // text is what a reader hears in place of the icon.
            <a
              key={social.id}
              href={social.url}
              className={styles.social}
              style={{
                width: side,
                height: side,
                // Wider apart the smaller they get, so the space between two
                // icons never closes below what a finger needs.
                marginInline: socialGap(side),
                // Half the button, so the glyph keeps its proportion at any
                // size the section is set to.
                fontSize: Math.round(side / 2),
                // Round unless the section says otherwise: half the button's
                // side is a circle, which is what a new social row starts at.
                borderRadius: cornersCss(
                  block.itemRadius ?? evenCorners(side / 2),
                ),
                ...VARIANT_STYLE[variantOf(block)](
                  emailStyles,
                  groundOf(block),
                ),
                // A fill chosen for the section wins over the icon style's own.
                ...(variantOf(block) === "fill" &&
                (block.fillCustom || block.fill)
                  ? {
                      background:
                        block.fillCustom ??
                        emailStyles[block.fill as NonNullable<Block["fill"]>],
                      // And the glyph goes with it: the icon style's own colour
                      // is the accent's light, which would be white on white
                      // once the fill is the sheet's own. A fill picked by hand
                      // has no pairing, so it falls back to the sheet's text
                      // colour — the same fallback a hand-picked ground gets
                      // everywhere else, and what the panel marks as chosen.
                      ...(block.ink || block.inkCustom
                        ? {}
                        : {
                            color:
                              emailStyles[
                                block.fillCustom
                                  ? "textOnBackground"
                                  : INK_ON[
                                      block.fill as NonNullable<Block["fill"]>
                                    ]
                              ],
                          }),
                    }
                  : {}),
                // A colour chosen for the block wins over the icon style's own,
                // which is only a sensible default for it.
                ...(block.inkCustom || block.ink ? { color: ink } : {}),
              }}
              aria-label={social.alt}
              title={social.alt}
            >
              {SOCIAL_ICONS[social.network]}
            </a>
          ))}
        </p>
      );
    }
    default:
      return <Paragraphs text={block.text} />;
  }
}

/**
 * The colour a block's words are read in.
 *
 * A button takes the one for its fill, since that is what its label stands on;
 * every other block the one for its own ground. A ground picked by hand has no
 * pairing of its own — the letter cannot know how dark it is — so it falls back
 * to the sheet's text colour, which is what the panel offers as chosen there.
 */
export function blockInk(block: Block, emailStyles: EmailStyles): string {
  if (block.inkCustom) return block.inkCustom;

  // Only a filled block stands its words on a fill; an outlined one, or a bare
  // label, stands them on the block's own ground like everything else.
  const onFill =
    (block.type === "button" || block.type === "social") &&
    variantOf(block) === "fill";
  const inkFor = onFill ? (block.fill ?? "brand") : block.bg;
  const handPicked = onFill ? block.fillCustom : block.bgCustom;

  return emailStyles[
    block.ink ??
      (handPicked
        ? "textOnBackground"
        : (INK_ON[inkFor ?? "Default"] ?? "textOnBackground"))
  ];
}

/**
 * The colour the ground itself would be read in, whatever the block's words
 * have been set to.
 *
 * The empty-picture band and its mark are a tint of this. They used to be a
 * tint of `currentColor`, which is the block's ink — so setting a paragraph in
 * the brand tinted the picture band beside it brand as well. A placeholder is
 * part of the surface, not part of the writing, and it only has to stay legible
 * on the ground it sits on.
 */
function groundInk(block: Block, emailStyles: EmailStyles): string {
  return emailStyles[
    block.bgCustom
      ? "textOnBackground"
      : (INK_ON[block.bg ?? "Default"] ?? "textOnBackground")
  ];
}

/** The band a block draws: its ground, its air, its corners and its ink. */
export function blockStyle(
  block: Block,
  emailStyles: EmailStyles,
): CSSProperties {
  return {
    ["--letter-ground-ink" as string]: groundInk(block, emailStyles),
    // The letter's own spacing unless the section answers for itself — except
    // a spacer, which is nothing but space and keeps none around it.
    padding:
      block.type === "spacer"
        ? 0
        : edgesCss(block.padding ?? emailStyles.padding),
    // A colour of the block's own wins over the letter's three grounds.
    background:
      block.bgCustom ?? BACKGROUNDS[block.bg ?? "Default"](emailStyles),
    borderRadius: cornersCss(block.radius ?? emailStyles.radius),
    color: blockInk(block, emailStyles),
  };
}

export default function LetterBlock({
  block,
  emailStyles,
  selected,
  preview,
  onSelect,
  onMove,
  onDuplicate,
  onRemove,
  canRemove,
  canMoveUp,
  canMoveDown,
}: LetterBlockProps) {
  const { t } = useTranslation();
  const {
    setNodeRef,
    listeners,
    attributes,
    isDragging,
    isOver,
    activeIndex,
    index,
  } = useSortable({ id: block.id, disabled: preview });

  // A section dragged out of the Blocks panel is not part of this sortable
  // list, so useSortable reports activeIndex -1 for it; it is told apart by its
  // id, which the panel prefixes.
  const { active } = useDndContext();
  const fromPanel = String(active?.id ?? "").startsWith("new:");

  // Nothing moves until the drag is dropped: the transform useSortable offers
  // is deliberately not applied, so the letter stays as it is and the only
  // thing that changes is a line showing where the block would land. The
  // reorder itself happens in the builder's onDragEnd.
  const showDropLine =
    isOver && (fromPanel || (activeIndex !== -1 && activeIndex !== index));

  const ink = blockInk(block, emailStyles);

  const style: CSSProperties = {
    ...blockStyle(block, emailStyles),
    opacity: isDragging ? 0.5 : 1,
  };

  const {
    token: { colorError },
  } = theme.useToken();

  const actions: {
    key: string;
    title: string;
    icon: React.ReactNode;
    onClick: () => void;
    /** Destructive: the icon carries colorError, the button stays plain. */
    danger?: boolean;
    disabled?: boolean;
  }[] = [
    {
      key: "up",
      title: t("pages.campaigns.builder.moveUp"),
      icon: <ArrowUpOutlined />,
      onClick: () => onMove(-1),
      disabled: !canMoveUp,
    },
    {
      key: "down",
      title: t("pages.campaigns.builder.moveDown"),
      icon: <ArrowDownOutlined />,
      onClick: () => onMove(1),
      disabled: !canMoveDown,
    },
    {
      key: "duplicate",
      title: t("pages.campaigns.builder.duplicate"),
      icon: <CopyOutlined />,
      onClick: onDuplicate,
    },
    {
      key: "remove",
      title: t("pages.campaigns.builder.remove"),
      icon: <DeleteOutlined />,
      onClick: onRemove,
      danger: true,
      disabled: !canRemove,
    },
  ];

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${styles.block} ${preview ? "" : styles.draggable} ${
        selected && !preview ? styles.selected : ""
      }`}
      // The whole section is a handle, not only the grip in its toolbar. The
      // sensor waits for a few pixels of travel before a drag starts, so a
      // click still selects.
      {...(preview ? {} : attributes)}
      {...(preview ? {} : listeners)}
      // The click stops here: the canvas underneath clears the selection when it
      // is clicked, and without this the block's own selection was undone by
      // its own click bubbling up to it.
      onClick={
        preview
          ? undefined
          : (event) => {
              event.stopPropagation();
              onSelect();
            }
      }
      // The builder finds a block by this to scroll to it once it is added.
      data-block-id={block.id}
      role={preview ? undefined : "button"}
      tabIndex={preview ? undefined : 0}
      onKeyDown={(e) => {
        if (!preview && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onSelect();
        }
      }}
    >
      {showDropLine ? (
        <span
          aria-hidden
          // A new section lands in front of the one it was dropped on, so its
          // line is always the one above.
          className={`${styles.dropLine} ${
            fromPanel || activeIndex > index
              ? styles.dropBefore
              : styles.dropAfter
          }`}
        />
      ) : null}

      <BlockContent block={block} emailStyles={emailStyles} ink={ink} />

      {selected && !preview ? (
        // Space.Compact is antd's own group of buttons: it joins them into one
        // control and takes a direction, so the column is the component's, not
        // a stack we spaced ourselves.
        <Space.Compact
          direction="vertical"
          className={styles.toolbar}
          onClick={(e) => e.stopPropagation()}
          // The toolbar is inside the section, which is now a drag handle:
          // without this, pressing Duplicate and moving the pointer a little
          // would start dragging the section instead. The grip below keeps its
          // own listeners, which run before this one on the way up.
          onPointerDown={(e) => e.stopPropagation()}
        >
          <Tooltip title={t("pages.campaigns.builder.drag")}>
            <Button
              type="text"
              className={styles.drag}
              icon={<HolderOutlined />}
              // The only control in this toolbar without a name of its own: a
              // tooltip is not announced as one, and every other icon button in
              // the app carries this.
              aria-label={t("pages.campaigns.builder.drag")}
              {...attributes}
              {...listeners}
            />
          </Tooltip>

          {actions.map(({ key, title, icon, onClick, danger, disabled }) => (
            <Tooltip key={key} title={title}>
              <Button
                // Text buttons, like the icon buttons everywhere else in the
                // app: the hover is a tint behind the glyph rather than a
                // bordered control lighting up.
                type="text"
                disabled={disabled}
                // The colour marks the action, not the control: only the glyph
                // turns red, so the row of buttons stays one group.
                icon={
                  danger && !disabled ? (
                    // Only while the action is available: a disabled button
                    // greys its own contents, and a red glyph inside one reads
                    // as still clickable.
                    <span style={{ color: colorError }}>{icon}</span>
                  ) : (
                    icon
                  )
                }
                onClick={(e) => {
                  e.stopPropagation();
                  onClick();
                }}
              />
            </Tooltip>
          ))}
        </Space.Compact>
      ) : null}
    </div>
  );
}
